'use client';
import React, { useEffect } from 'react';
import Link from 'next/link';
import { z } from 'zod';
import Cookies from 'js-cookie';
import { useForm, SubmitHandler } from 'react-hook-form';
import { useLocale, useTranslations } from 'next-intl';
import client from '@/clients/customer/api-client';
import { useUserStore } from '@/stores/customer/user';
import { customerLoginSchema } from '@/lib/validations/customer/authSchemas';
import { storage_keys } from '@/constant/configs';
import Button from '@ui/Button';
import { MemberCopy } from '@ui/Icons';
import InputField from '@/components/shared/InputField';
import { LoadingSpinner } from '@ui/LoadingSpinner';
import AuthUserGuard from '@/components/shared/AuthUserGuard';
import { getDeviceToken, handleErrors } from '@/lib/helpers/functions';
import { zodResolver } from '@hookform/resolvers/zod';
import { storeFront } from '@/lib/utils/routeLinks';
import useCustomerAuth from '@/hooks/useCustomerAuth';
import { useSearchParams } from 'next/navigation';

interface UserLoginFormProps {
  userAuthToken?: string;
}

export const UserLoginForm = ({ userAuthToken }: UserLoginFormProps) => {
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const setUser = useUserStore((state) => state.setUser);
  const { handleCustomerLogin, checkAuthAndRedirect } = useCustomerAuth();
  const redirectURL = useSearchParams().get('redirect');

  const [showWarnUser, setShowWarnUser] = React.useState<boolean>(false);

  const t = useTranslations();
  const lang = useLocale();
  const schema = customerLoginSchema(lang as 'de' | 'en');
  type FormData = z.infer<typeof schema>;

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      username: '',
      password: '',
    },
  });

  useEffect(() => {
    checkAuthAndRedirect(userAuthToken);
  }, [checkAuthAndRedirect]);

  const onSubmit: SubmitHandler<FormData> = (data, event) => {
    event?.preventDefault();

    if (Cookies.get(storage_keys.restaurant_token)) {
      setShowWarnUser(true);
      return;
    }

    const deviceToken = getDeviceToken();
    if (deviceToken) {
      data['device_token'] = deviceToken;
    }

    setIsLoading(true);

    client
      .post('/login', data)
      .then((res) => {
        handleCustomerLogin(res, setUser, redirectURL || '/');
      })
      .catch((error) => {
        setIsLoading(false);
        handleErrors({ type: 'form', setError, errors: error.response });
      });
  };

  return (
    <form
      className="mb-6 w-full 2xl:mb-[30px]"
      onSubmit={handleSubmit(onSubmit)}
    >
      <InputField<FormData>
        name="username"
        type="email"
        label="E-mail"
        placeholder="sample@name.com"
        register={register}
        errors={errors}
      />
      <InputField<FormData>
        name="password"
        type="password"
        label="Passwort"
        placeholder="***************"
        register={register}
        errors={errors}
      />

      <div className="mb-[28px] flex w-full items-center justify-between 2xl:mb-[40px]">
        <Link
          href={storeFront.forgotPassword.de()}
          className="p-md hover:text-primary-700"
        >
          {t('forgot_password')}
        </Link>
      </div>

      <Button type="submit" gradient className="group" disabled={isLoading}>
        <span className="mr-[14px] flex items-center gap-x-[5px]">
          {isLoading && <LoadingSpinner />}
          {t('login')}
        </span>
        <MemberCopy height={21} />
      </Button>
      {showWarnUser && (
        <AuthUserGuard
          type="customer"
          showWarning={showWarnUser}
          setShowWarning={setShowWarnUser}
        />
      )}
    </form>
  );
};
